/* WESN BOK 3D inspector — self-hosted build for the theme section.
   Ported from the WESN-BOK-Website-Embed package: same model, textures, copy and behaviour, no iframe.
   three.js r180 and the model live on the store's own CDN (Shopify Files). */
import * as THREE from 'https://cdn.shopify.com/s/files/1/2676/7168/files/bok3d-three.module.js';
import { GLTFLoader } from 'https://cdn.shopify.com/s/files/1/2676/7168/files/bok3d-GLTFLoader.js';
import { OrbitControls } from 'https://cdn.shopify.com/s/files/1/2676/7168/files/bok3d-OrbitControls.js';
import { RoomEnvironment } from 'https://cdn.shopify.com/s/files/1/2676/7168/files/bok3d-RoomEnvironment.js';

/* ---- features.js (copy, focus points and part mapping) ---- */
// Coordinates use the original model's meters and Y-up orientation.
// Add a detail here, its navigation button, and its part mapping to extend the section.
const FEATURES = {
  blade: {
    number:'01', label:'BLADE', title:['BLADE'],
    supportingByMaterial:{
      titanium:['MAGNACUT BLADE STEEL','DROP POINT SHAPE','SATIN FINISH'],
      g10:['MAGNACUT BLADE STEEL','DROP POINT SHAPE','STAINLESS FINISH'],
    },
    target:[-.043,.001,0], direction:[-.10,.94,.34], span:.112,
    point:[-.052,.0018,.0008], parts:[15],
  },
  button: {
    number:'02', label:'BUTTON LOCK', title:['BUTTON LOCK'],
    supporting:['BUTTON LINER LOCK SYSTEM'],
    body:'The BOK is built around its button lock system, featuring an internal liner to engage and release the lock. Just press to open the blade, and press again to close.',
    side:'front',
    target:[.01034,.0049,-.0092], direction:[-.15,.95,.27], span:.043,
    point:[.01034,.0056,-.0092], parts:[4],
  },
  handle: {
    number:'03', label:'HANDLE MATERIAL', title:['HANDLE MATERIAL'],
    supportingByMaterial:{titanium:['GRADE 5 TITANIUM SCALES'],g10:['BLACK G10 SCALES']},
    bodyByMaterial:{
      titanium:'Lightweight, durable, and heat treated to last a lifetime. Feel as good as they look.',
      g10:'Lightweight and highly durable. A texture that adds an incredible layer to the design and feel of the BOK handle.',
    },
    target:[.04,.004,0], direction:[.10,.92,.38], span:.120,
    point:[.041,.0063,0], parts:[11,12],
  },
  stud: {
    number:'04', label:'THUMB STUD', title:['THUMB STUD'],
    supporting:['REMOVABLE THUMB STUD'],
    body:'Flick out the BOK’s thumb stud to open your knife with flair. Removable for those who prefer a more minimalist carry.',
    target:[-.0156,.001,-.0102], direction:[-.35,.84,.41], span:.047,
    point:[-.0156,.0048,-.0103], parts:[13,14],
  },
  lanyard: {
    number:'05', label:'LANYARD ANCHOR', title:['LANYARD ANCHOR'],
    supporting:['INTEGRATED LANYARD POINT'],
    body:'The BOK trades the traditional lanyard hole for an unseen lanyard anchor, placed just inside the handle on the bottom of the frame. Just weave through your lanyard string, and tie as normal.',
    target:[.084,0,0], direction:[.92,.15,.36], span:.049,
    point:[.088,.0063,.004], parts:[3],
  },
  clip: {
    number:'06', label:'POCKET CLIP', title:['POCKET CLIP'],
    supporting:['DEEP CARRY & REMOVABLE'],
    body:'The BOK pocket clip is deep carry style, allowing for ease of carry inside your pocket. Additionally, it is easily removable / switchable. Allowing you to take it off completely, or switch to the other side of the handle. Allowing for true ambidextrous carry.',
    side:'back',
    target:[.07,-.008,0], direction:[.12,-.94,.32], span:.067,
    point:[.07,-.0108,0], parts:[8],
  },
};
const OVERVIEW = {target:[.003,0,0],direction:[-.055,.91,.41],span:.217};
const MATERIALS = {titanium:{label:'TITANIUM',variant:'Titanium'},g10:{label:'BLACK G10',variant:'Black G10'}};
function featureForPart(part,x=0){
  if(x>.084 && [11,12].includes(part))return 'lanyard';
  if(x>.076 && [0,1,7,10,16,17,19].includes(part))return 'lanyard';
  return Object.keys(FEATURES).find(key=>FEATURES[key].parts.includes(part))??null;
}

/* ---- end features ---- */

const vector = a => new THREE.Vector3(...a);
const clamp = (value,min,max) => Math.max(min,Math.min(max,value));

export class BOKInspector {
  constructor(root) {
    this.root=root;
    this.query=selector=>root.querySelector(selector);
    this.canvas=this.query('#bok-canvas');
    this.wrap=this.query('#canvas-wrap');
    this.state={material:'titanium',feature:null,ready:false};
    this.reducedMotion=matchMedia('(prefers-reduced-motion: reduce)');
    this.needsRender=true;this.visible=true;this.markers=new Map();this.meshes=[];this.bindings=[];
    this.pointerIds=new Set();this.pointerDown=null;this.multiTouch=false;
    this.wireInterface();
    this.initialize().catch(error=>this.showError(error));
  }

  wireInterface() {
    for(const [key,feature] of Object.entries(FEATURES)) {
      const button=document.createElement('button');
      button.type='button';button.className='hotspot';button.dataset.feature=key;
      button.setAttribute('aria-label',`Inspect ${feature.label.toLowerCase()}`);
      button.setAttribute('aria-pressed','false');button.tabIndex=-1;
      const disc=document.createElement('span');disc.className='hotspot-disc';disc.textContent='+';disc.setAttribute('aria-hidden','true');
      const tip=document.createElement('span');tip.className='hotspot-tip';tip.textContent=feature.label;
      button.append(disc,tip);this.query('#hotspots').append(button);this.markers.set(key,button);
    }
    this.root.querySelectorAll('[data-feature]').forEach(button=>button.addEventListener('click',()=>{
      this.selectFeature(button.dataset.feature);
      if(button.closest('.feature-list')&&matchMedia('(max-width:760px)').matches)this.query('.stage').scrollIntoView({block:'start',behavior:this.reducedMotion.matches?'instant':'smooth'});
    }));
    this.root.querySelectorAll('input[name="material"]').forEach(input=>input.addEventListener('change',()=>this.setMaterial(input.value)));
    this.query('#reset-view').addEventListener('click',()=>this.reset());
    this.query('#zoom-in').addEventListener('click',()=>this.zoom(.8));
    this.query('#zoom-out').addEventListener('click',()=>this.zoom(1.25));
    this.query('#retry').addEventListener('click',()=>location.reload());
    this.root.addEventListener('keydown',event=>{if(event.key==='Escape'){event.preventDefault();this.reset();}});
    this.canvas.addEventListener('keydown',event=>this.onKey(event));
    this.canvas.addEventListener('webglcontextlost',event=>{event.preventDefault();this.showError(new Error('The 3D view was interrupted. Reload it to continue.'));});
    this.canvas.addEventListener('pointerdown',event=>{
      this.pointerIds.add(event.pointerId);
      if(this.pointerIds.size>1)this.multiTouch=true;
      this.pointerDown={x:event.clientX,y:event.clientY,time:performance.now()};
    });
    this.canvas.addEventListener('pointerup',event=>{
      const start=this.pointerDown;
      if(start&&!this.multiTouch&&Math.hypot(start.x-event.clientX,start.y-event.clientY)<7&&performance.now()-start.time<700&&this.state.ready) {
        const hit=this.pick(event);
        if(hit?.feature)this.selectFeature(hit.feature);
      }
      this.pointerIds.delete(event.pointerId);
      if(this.pointerIds.size===0){this.multiTouch=false;this.pointerDown=null;}
    });
    this.canvas.addEventListener('pointercancel',()=>{this.pointerIds.clear();this.pointerDown=null;this.multiTouch=false;});
    let lastHover=0;
    this.canvas.addEventListener('pointermove',event=>{
      if(event.pointerType!=='mouse'||this.pointerIds.size||!this.state.ready||performance.now()-lastHover<80)return;
      lastHover=performance.now();this.canvas.style.cursor=this.pick(event)?.feature?'pointer':'grab';
    });
    document.addEventListener('visibilitychange',()=>{this.needsRender=true;});
  }

  async initialize() {
    this.renderer=new THREE.WebGLRenderer({canvas:this.canvas,antialias:true,alpha:false,powerPreference:'high-performance'});
    this.renderer.setPixelRatio(Math.min(devicePixelRatio||1,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.toneMapping=THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure=1.08;
    this.renderer.shadowMap.enabled=true;
    this.renderer.shadowMap.type=THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate=false;
    this.scene=new THREE.Scene();this.scene.background=new THREE.Color('#f1eee5');
    this.camera=new THREE.PerspectiveCamera(34,1,.0005,8);
    this.controls=new OrbitControls(this.camera,this.canvas);
    this.controls.enableDamping=true;this.controls.dampingFactor=.09;
    this.controls.enablePan=false;this.controls.rotateSpeed=.58;this.controls.zoomSpeed=.7;
    this.controls.minDistance=.025;this.controls.maxDistance=.8;
    this.controls.minPolarAngle=.045;this.controls.maxPolarAngle=Math.PI-.045;
    this.controls.addEventListener('change',()=>{this.needsRender=true;});
    this.controls.addEventListener('start',()=>{this.tween=null;this.controls.enableDamping=true;});
    this.raycaster=new THREE.Raycaster();
    this.ndc=new THREE.Vector2();
    this.setupLight();
    this.resizeObserver=new ResizeObserver(()=>this.resize());this.resizeObserver.observe(this.wrap);
    this.intersectionObserver=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.needsRender=true;},{rootMargin:'120px'});this.intersectionObserver.observe(this.root);
    this.resize();this.flyTo(OVERVIEW,true);
    this.frame=now=>{this.renderFrame(now);this.raf=requestAnimationFrame(this.frame);};this.raf=requestAnimationFrame(this.frame);
    const manager=new THREE.LoadingManager();
    manager.onProgress=(_url,loaded,total)=>{this.query('#load-progress').style.width=`${Math.min(94,8+86*loaded/total)}%`;};
    const loader=new GLTFLoader(manager);
    loader.setRequestHeader({Accept:'image/png,image/jpeg;q=0.9,*/*;q=0.5'}); /* ask the CDN for the original PNG textures rather than a lossy WebP transcode */
    const gltf=await loader.loadAsync(this.root.dataset.model);
    this.model=gltf.scene;this.model.updateMatrixWorld(true);this.scene.add(this.model);
    const materialTasks=[];
    this.model.traverse(object=>{
      if(!object.isMesh)return;
      object.castShadow=true;object.receiveShadow=true;this.meshes.push(object);
      const association=gltf.parser.associations.get(object);
      if(association?.meshes===undefined||association.primitives===undefined)return;
      const primitive=gltf.parser.json.meshes[association.meshes].primitives[association.primitives];
      const mappings=primitive.extensions?.KHR_materials_variants?.mappings;
      if(!mappings)return;
      materialTasks.push((async()=>{
        const variants={};
        for(const mapping of mappings) {
          const material=await gltf.parser.getDependency('material',mapping.material);
          for(const index of mapping.variants)variants[gltf.parser.json.extensions.KHR_materials_variants.variants[index].name]=material;
        }
        this.bindings.push({mesh:object,variants});
      })());
    });
    await Promise.all(materialTasks);
    if(this.bindings.length!==4)throw new Error('The model’s material choices could not be prepared. Please reload.');
    this.state.ready=true;this.setMaterial(this.state.material);
    this.query('#load-label').textContent='PREPARING YOUR VIEW';
    this.query('#load-progress').style.width='98%';
    if(this.renderer.compileAsync)await this.renderer.compileAsync(this.scene,this.camera);
    this.renderer.shadowMap.needsUpdate=true;this.needsRender=true;
    this.wrap.setAttribute('aria-busy','false');
    this.query('#load-progress').style.width='100%';this.query('#load-state').classList.add('done');
    this.query('#load-state').setAttribute('aria-hidden','true');
    for(const id of ['#zoom-in','#zoom-out','#reset-view'])this.query(id).disabled=false;
    this.flyTo(this.state.feature?FEATURES[this.state.feature]:OVERVIEW,true);
    this.root.dispatchEvent(new CustomEvent('bok:ready',{bubbles:true,detail:this.getState()}));
  }

  setupLight() {
    const pmrem=new THREE.PMREMGenerator(this.renderer);
    const room=new RoomEnvironment();this.environment=pmrem.fromScene(room,.04);
    this.scene.environment=this.environment.texture;this.scene.environmentIntensity=.9;
    room.dispose();pmrem.dispose();
    const light=new THREE.DirectionalLight('#fffaf0',2.4);light.position.set(-.09,.3,.1);
    light.target.position.set(.003,0,0);light.castShadow=true;
    Object.assign(light.shadow.camera,{left:-.18,right:.18,top:.14,bottom:-.14,near:.03,far:.8});
    light.shadow.mapSize.set(2048,2048);light.shadow.normalBias=.00014;light.shadow.bias=-.00006;
    this.scene.add(light,light.target);
    const fill=new THREE.DirectionalLight('#ffffff',.6);fill.position.set(.1,.06,-.2);this.scene.add(fill);
    this.floor=new THREE.Mesh(new THREE.PlaneGeometry(5,5),new THREE.ShadowMaterial({color:'#4b4a43',opacity:.17,depthWrite:false}));
    this.floor.rotation.x=-Math.PI/2;this.floor.position.y=-.018;this.floor.receiveShadow=true;this.scene.add(this.floor);
  }

  getState(){return {...this.state};}

  setMaterial(material) {
    if(!MATERIALS[material])throw new Error(`Unknown BOK material: ${material}`);
    this.state.material=material;
    if(this.state.ready)for(const {mesh,variants} of this.bindings)mesh.material=variants[MATERIALS[material].variant];
    this.root.querySelectorAll('input[name="material"]').forEach(input=>{input.checked=input.value===material;});
    this.query('#material-label').textContent=MATERIALS[material].label;
    this.updateDetail();this.needsRender=true;
    this.root.dispatchEvent(new CustomEvent('bok:materialchange',{bubbles:true,detail:this.getState()}));
  }

  selectFeature(key) {
    if(!FEATURES[key])throw new Error(`Unknown BOK feature: ${key}`);
    this.state.feature=key;this.updateDetail();
    if(this.state.ready)this.flyTo(FEATURES[key]);
    this.root.dispatchEvent(new CustomEvent('bok:detailchange',{bubbles:true,detail:this.getState()}));
  }

  updateDetail() {
    const feature=this.state.feature?FEATURES[this.state.feature]:null;
    this.query('#detail-display').classList.toggle('is-selected',!!feature);
    const total=String(Object.keys(FEATURES).length).padStart(2,'0');
    this.query('#detail-index').textContent=feature?`DETAIL ${feature.number} / ${total}`:'A CLOSER LOOK';
    const lines=feature?feature.title:['SELECT A','DETAIL.'];
    const title=this.query('#detail-title');title.replaceChildren();
    lines.forEach((line,i)=>{if(i)title.append(document.createElement('br'));title.append(document.createTextNode(line));});
    const secondary=this.query('#detail-secondary');secondary.textContent=feature?(feature.supportingByMaterial?.[this.state.material]??feature.supporting??[]).join('\n'):'Select a point on the knife or a detail below.';secondary.hidden=!secondary.textContent;
    const body=this.query('#detail-body');body.textContent=feature?(feature.bodyByMaterial?.[this.state.material]??feature.body??''):'';body.hidden=!body.textContent;
    this.query('#view-label').textContent=feature?feature.label:'FULL VIEW';
    this.query('#selection-count').textContent=`${feature?.number??'00'} — ${total}`;
    this.root.querySelectorAll('[data-feature]').forEach(button=>{
      const active=button.dataset.feature===this.state.feature;button.setAttribute('aria-pressed',String(active));
      const symbol=button.querySelector('.feature-symbol');if(symbol)symbol.textContent=active?'−':'+';
    });
    this.needsRender=true;
  }

  reset() {
    this.state.feature=null;this.updateDetail();
    if(this.state.ready)this.flyTo(OVERVIEW);
    this.root.dispatchEvent(new CustomEvent('bok:detailchange',{bubbles:true,detail:this.getState()}));
  }

  viewPosition(view) {
    const target=vector(view.target);
    const distance=clamp(view.span/(2*Math.tan(THREE.MathUtils.degToRad(this.camera.fov/2))*this.camera.aspect),.04,.75);
    return {target,position:target.clone().add(vector(view.direction).normalize().multiplyScalar(distance))};
  }

  flyTo(view,instant=false) {
    const next=this.viewPosition(view);
    this.controls.enableDamping=false;this.controls.update();
    if(instant||this.reducedMotion.matches) {
      this.tween=null;this.camera.position.copy(next.position);this.controls.target.copy(next.target);this.controls.update();this.controls.enableDamping=true;this.needsRender=true;return;
    }
    this.tween={start:performance.now(),duration:1150,fromPosition:this.camera.position.clone(),fromTarget:this.controls.target.clone(),...next};
    this.needsRender=true;
  }

  zoom(factor) {
    if(!this.state.ready)return;
    this.tween=null;this.controls.enableDamping=true;
    const offset=this.camera.position.clone().sub(this.controls.target);
    offset.setLength(clamp(offset.length()*factor,this.controls.minDistance,this.controls.maxDistance));
    this.camera.position.copy(this.controls.target).add(offset);this.controls.update();this.needsRender=true;
  }

  onKey(event) {
    if(!this.state.ready)return;
    if(['+','=','-','_'].includes(event.key)){event.preventDefault();this.zoom(['+','='].includes(event.key)?.8:1.25);return;}
    if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key))return;
    event.preventDefault();this.tween=null;this.controls.enableDamping=true;
    const spherical=new THREE.Spherical().setFromVector3(this.camera.position.clone().sub(this.controls.target));
    spherical.theta+=event.key==='ArrowLeft'?-.12:event.key==='ArrowRight'?.12:0;
    spherical.phi=clamp(spherical.phi+(event.key==='ArrowUp'?-.09:event.key==='ArrowDown'?.09:0),.045,Math.PI-.045);
    this.camera.position.copy(this.controls.target).add(new THREE.Vector3().setFromSpherical(spherical));this.controls.update();this.needsRender=true;
  }

  pick(event) {
    const bounds=this.canvas.getBoundingClientRect();
    this.ndc.set((event.clientX-bounds.left)/bounds.width*2-1,-(event.clientY-bounds.top)/bounds.height*2+1);
    this.raycaster.setFromCamera(this.ndc,this.camera);
    const hit=this.raycaster.intersectObjects(this.meshes,false)[0];if(!hit)return null;
    let node=hit.object;while(node&&node.userData.source_part_id===undefined)node=node.parent;
    return {hit,feature:featureForPart(node?.userData.source_part_id,hit.point.x)};
  }

  resize() {
    if(!this.renderer)return;
    const rect=this.wrap.getBoundingClientRect();if(!rect.width||!rect.height)return;
    this.renderer.setSize(rect.width,rect.height,false);this.camera.aspect=rect.width/rect.height;this.camera.updateProjectionMatrix();
    this.width=rect.width;this.height=rect.height;
    this.flyTo(this.state.feature?FEATURES[this.state.feature]:OVERVIEW,!this.tween);this.needsRender=true;
  }

  updateMarkers() {
    for(const [key,button] of this.markers) {
      let visible=this.state.ready&&!this.state.feature;
      const feature=FEATURES[key],position=vector(feature.point);
      if(feature.side==='back')visible=visible&&this.camera.position.y<0;
      else if(feature.side==='front')visible=visible&&this.camera.position.y>=0;
      else if(this.camera.position.y<0)position.y=-Math.abs(position.y);
      const projected=position.project(this.camera);
      const x=(projected.x*.5+.5)*this.width,y=(-projected.y*.5+.5)*this.height;
      visible=visible&&projected.z>-1&&projected.z<1&&x>22&&x<this.width-22&&y>52&&y<this.height-72;
      button.classList.toggle('visible',visible);button.tabIndex=visible?0:-1;
      button.setAttribute('aria-hidden',String(!visible));
      if(visible){button.style.left=`${x}px`;button.style.top=`${y}px`;}
    }
  }

  renderFrame(now) {
    if(document.hidden||!this.visible)return;
    if(this.tween) {
      const t=clamp((now-this.tween.start)/this.tween.duration,0,1);const ease=t*t*t*(t*(t*6-15)+10);
      this.camera.position.lerpVectors(this.tween.fromPosition,this.tween.position,ease);
      this.controls.target.lerpVectors(this.tween.fromTarget,this.tween.target,ease);this.needsRender=true;
      if(t===1){this.tween=null;this.controls.enableDamping=true;}
    }
    this.controls.update();
    if(!this.needsRender)return;
    this.floor.visible=this.camera.position.y>-.009;
    this.renderer.render(this.scene,this.camera);this.updateMarkers();this.needsRender=false;
  }

  showError(error) {
    console.error('BOK inspector:',error);
    this.state.ready=false;this.wrap.setAttribute('aria-busy','false');
    const load=this.query('#load-state');load.classList.remove('done');load.classList.add('error');load.removeAttribute('aria-hidden');
    this.query('#load-label').textContent=error.message?.includes('WebGL')?'This browser could not open the 3D view. Try another browser or enable graphics acceleration.':'The 3D view could not load. Please try again.';
    this.query('#retry').hidden=false;
    for(const id of ['#zoom-in','#zoom-out','#reset-view'])this.query(id).disabled=true;
  }
}

const root=document.getElementById('bok3d');
if(root&&root.dataset.model){
  const start=()=>{ if(root.bokInspector)return; root.bokInspector=new BOKInspector(root); root.bokInspector.updateDetail(); };
  if('IntersectionObserver' in window){
    const io=new IntersectionObserver(entries=>{ if(entries.some(e=>e.isIntersecting)){ io.disconnect(); start(); } },{rootMargin:'700px 0px'});
    io.observe(root);
  } else start();
}
